import {useEffect, useMemo, useState, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {useCustomTheme} from 'context/customTheme';
import {useReservation} from 'context/reservation';
import {LANGUAGE_CODES} from '@guestapp/core';
import {type Location} from '@guestapp/sdk';
import {useGuruWalkTourDetector} from './useGuruWalkTourDetector';
import {injectStyles, removeStyles} from './utils';
import {Banner} from './Banner';
import shadowRootStyles from './shadow-root-styles.module.css?inline';
import styles from './styles.module.css';

const GURUWALK_AFFILIATE = import.meta.env.VITE_GURUWALK_AFFILIATE;

const SUPPORTED_LANGUAGES = [
  LANGUAGE_CODES.en,
  LANGUAGE_CODES.es,
  LANGUAGE_CODES.de,
  LANGUAGE_CODES.it,
];

const getLanguage = (lang: string) => {
  const appLanguage = lang.slice(0, 2);
  if (SUPPORTED_LANGUAGES.some(lang => lang === appLanguage)) {
    return appLanguage;
  }
  return LANGUAGE_CODES.en;
};

type Props = {
  location: Location;
  housingId: string;
};

const getCityOrCountry = (location: Location) => {
  return location.city || location.country?.name;
};

export const GuruwalkWidget = ({location}: Props) => {
  const stylesLoadedRef = useRef<boolean>(false);
  const {i18n} = useTranslation();
  const {data: reservation} = useReservation();
  const {finalTheme} = useCustomTheme();
  const lang = getLanguage(i18n.language);
  const [hasTours, setHasTours] = useState(false);

  useGuruWalkTourDetector((hasTours: boolean) => {
    if (hasTours) {
      injectStyles(shadowRootStyles);
      stylesLoadedRef.current = true;
      setHasTours(true);
    } else {
      removeStyles();
      stylesLoadedRef.current = false;
      setHasTours(false);
    }
  });

  useEffect(() => {
    if (!document.getElementById('guruwalk-widget')) {
      const script = document.createElement('script');
      script.id = 'guruwalk-widget';
      script.src = '/assets/js/widget.min.js';
      script.defer = true;
      document.body.appendChild(script);
    }
  }, []);

  useEffect(() => {
    if (stylesLoadedRef.current) {
      injectStyles(shadowRootStyles);
    }
  }, [lang]);

  const widget = useMemo(() => {
    if (!reservation) return null;

    return (
      <div
        className={hasTours ? styles.guruwalkWidget__wrapper : ''}
        style={{
          background: finalTheme.colors.branding.boxesModals,
          ...(!hasTours ? {visibility: 'hidden', height: 0} : {}),
        }}
      >
        {hasTours && <Banner />}
        <guruwalk-tour-cards
          key={lang}
          affiliate={`${GURUWALK_AFFILIATE}&referrer_campaign=${reservation?.id}`}
          city={getCityOrCountry(location)}
          lang={lang}
          text-color={finalTheme.colors.$fontBlack}
          primary-color={finalTheme.colors.branding.primary}
          secondary-color={finalTheme.colors.branding.fontSecondary}
          limit="3"
          description="none"
        ></guruwalk-tour-cards>
      </div>
    );
  }, [
    reservation,
    hasTours,
    finalTheme.colors.branding.boxesModals,
    finalTheme.colors.branding.primary,
    finalTheme.colors.branding.fontSecondary,
    finalTheme.colors.$fontBlack,
    lang,
    location,
  ]);

  return widget;
};
